import { useEffect, useState } from "react";
import type { ImageProps } from "./Image.types";
import { ImageSkeleton } from "./ImageSkeleton";

/**
 * Reusable Image component that handles:
 * - Loading state with skeleton animation
 * - Error fallback with default image
 * - All standard img props
 */
export const Image = ({
	src = "/no-image.png", // default image path
	alt,
	className = "",
	defaultSrc = "/no-image.png", // default fallback image path
	width,
	height,
	loading = "lazy",
	onLoad,
	onError,
	...rest
}: ImageProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we need src as a dependency
	useEffect(() => {
		setIsLoading(true);
		setHasError(false);
	}, [src]);

	const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
		setIsLoading(false);
		if (onLoad) onLoad(event);
	};

	const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
		setIsLoading(false);
		setHasError(true);
		if (onError) onError(event);
	};

	if (isLoading) {
		return (
			<div className={className}>
				<ImageSkeleton width={width} height={height} />

				<img
					src={src}
					alt={alt}
					className="hidden"
					onLoad={handleLoad}
					onError={handleError}
					{...rest}
				/>
			</div>
		);
	}

	if (hasError) {
		return (
			<img
				src={defaultSrc}
				alt={alt || "Default Image"}
				className={className}
				width={width}
				height={height}
				loading={loading}
				{...rest}
			/>
		);
	}

	return (
		<img
			src={src}
			alt={alt}
			className={className}
			width={width}
			height={height}
			loading={loading}
			onLoad={handleLoad}
			onError={handleError}
			{...rest}
		/>
	);
};
